import React, { useRef } from "react";
import toast from "react-hot-toast";
import { Formik, Form, Field, ErrorMessage, FormikValues } from "formik";
import * as Yup from "yup";
import { Spin } from "antd";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  SCreateAsset,
  SUpdateAsset,
  getAssets,
  getAssetCategories,
} from "../../../apis/app.service";
import AppButton from "../../sharedcomponents/Buttons/AppButton/AppButton";
import { useAssetsPage } from "../../../store/AssetsProvider";
import {
  IAsset,
  IAssetForm,
  IAssetCategory,
  ASSET_STATUSES,
} from "../core/models";

const assetSchema = Yup.object({
  category: Yup.string().required().label("Category"),
  assetNo: Yup.string().required().label("Asset No"),
  status: Yup.string().required().label("Status"),
  description: Yup.string().required().label("Description"),
});

// Suggest the next asset number for a category, e.g. LAP-12-25.
function suggestAssetNo(
  category: string,
  assets: IAsset[] = [],
  categories: IAssetCategory[] = []
): string {
  const prefix =
    categories.find((c) => c.name === category)?.prefix ||
    category.replace(/[^a-zA-Z]/g, "").slice(0, 3).toUpperCase() ||
    "AST";
  const year = new Date().getFullYear().toString().slice(-2);
  const highest = assets
    .filter((a) => a.assetNo?.toUpperCase().startsWith(`${prefix}-`))
    .map((a) => parseInt(a.assetNo.split("-")[1], 10))
    .filter((n) => !Number.isNaN(n))
    .reduce((max, n) => (n > max ? n : max), 0);
  return `${prefix}-${String(highest + 1).padStart(2, "0")}-${year}`;
}

export default function CreateEditAsset() {
  const { asset } = useAssetsPage();
  const { action, data } = asset;
  const modalRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();

  const { data: assets } = useQuery<IAsset[]>({
    queryKey: ["assets"],
    queryFn: () => getAssets(),
  });

  const { data: categories } = useQuery<IAssetCategory[]>({
    queryKey: ["asset-categories"],
    queryFn: getAssetCategories,
  });

  const closeModal = () => modalRef.current?.click();

  const onSuccess = (res: { message: string }) => {
    queryClient.invalidateQueries({ queryKey: ["assets"] });
    toast.success(res.message, { duration: 3000, position: "top-right" });
    closeModal();
  };
  const onError = (err: Error) =>
    toast.error(err.message, { duration: 3000, position: "top-right" });

  const createMutation = useMutation({
    mutationFn: SCreateAsset,
    onSuccess,
    onError,
  });
  const updateMutation = useMutation({
    mutationFn: SUpdateAsset,
    onSuccess,
    onError,
  });

  const submitForm = (values: IAssetForm, _actions: FormikValues) => {
    const payload = {
      category: values.category,
      assetNo: values.assetNo.trim(),
      status: values.status,
      description: values.description.trim(),
      serialNumber: values.serialNumber.trim(),
      supplier: values.supplier.trim(),
      purchaseDate: values.purchaseDate || undefined,
      totalCost: values.totalCost,
      currency: values.currency,
      notes: values.notes.trim(),
    };
    if (action === "edit") {
      updateMutation.mutate({ assetId: values.id!, body: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isEdit = action === "edit";

  return (
    <div className="modal fade" id="kt_modal_create_asset" aria-hidden="true">
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: "820px" }}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="asset-modal-title">
              <span className="text-capitalize">{isEdit ? "Edit" : "Add"}</span>
              Asset
            </h2>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              ref={modalRef}
            ></button>
          </div>

          <div className="modal-body">
            <Formik
              validationSchema={assetSchema}
              initialValues={data}
              onSubmit={submitForm}
              enableReinitialize
            >
              {(formik) => (
                <Form className="form" noValidate>
                  <div className="row mb-3">
                    <div className="col-md-6 form-group">
                      <label className="form-label fs-5">
                        <span className="required">Category</span>
                      </label>
                      <select
                        className="form-select"
                        name="category"
                        value={formik.values.category}
                        onChange={(e) => {
                          const category = e.target.value;
                          formik.setFieldValue("category", category);
                          // Auto-suggest asset number for new assets only.
                          if (!isEdit && !formik.touched.assetNo) {
                            formik.setFieldValue(
                              "assetNo",
                              suggestAssetNo(category, assets, categories)
                            );
                          }
                        }}
                      >
                        {categories?.map((c) => (
                          <option key={c._id} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <div className="text-danger">
                        <ErrorMessage name="category" />
                      </div>
                    </div>

                    <div className="col-md-6 form-group">
                      <label className="form-label fs-5">
                        <span className="required">Asset No</span>
                        {!isEdit && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 align-baseline"
                            onClick={() =>
                              formik.setFieldValue(
                                "assetNo",
                                suggestAssetNo(
                                  formik.values.category,
                                  assets,
                                  categories
                                )
                              )
                            }
                          >
                            suggest
                          </button>
                        )}
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        name="assetNo"
                        placeholder="e.g. LAP-01-25"
                      />
                      <div className="text-danger">
                        <ErrorMessage name="assetNo" />
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-12 form-group">
                      <label className="form-label fs-5">
                        <span className="required">
                          Description (including model number)
                        </span>
                      </label>
                      <Field
                        as="textarea"
                        rows={2}
                        className="form-control"
                        name="description"
                        placeholder="e.g. HP EliteBook 840 G8"
                      />
                      <div className="text-danger">
                        <ErrorMessage name="description" />
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6 form-group">
                      <label className="form-label fs-5">Serial Number</label>
                      <Field
                        type="text"
                        className="form-control"
                        name="serialNumber"
                      />
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="form-label fs-5">
                        <span className="required">Status</span>
                      </label>
                      <select
                        className="form-select"
                        name="status"
                        value={formik.values.status}
                        onChange={(e) =>
                          formik.setFieldValue("status", e.target.value)
                        }
                      >
                        {ASSET_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6 form-group">
                      <label className="form-label fs-5">Supplier</label>
                      <Field
                        type="text"
                        className="form-control"
                        name="supplier"
                      />
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="form-label fs-5">Date of Purchase</label>
                      <input
                        type="date"
                        className="form-control"
                        name="purchaseDate"
                        value={formik.values.purchaseDate}
                        onChange={(e) =>
                          formik.setFieldValue("purchaseDate", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-4 form-group">
                      <label className="form-label fs-5">Total Cost (Gross)</label>
                      <Field
                        type="number"
                        className="form-control"
                        name="totalCost"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="col-md-2 form-group">
                      <label className="form-label fs-5">Currency</label>
                      <Field
                        type="text"
                        className="form-control"
                        name="currency"
                        placeholder="UGX"
                      />
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="form-label fs-5">Notes</label>
                      <Field
                        type="text"
                        className="form-control"
                        name="notes"
                        placeholder="e.g. Functional & Available"
                      />
                    </div>
                  </div>

                  <div className="mx-auto d-flex justify-content-center mt-2">
                    <AppButton btnType="success" type="submit" disabled={isPending}>
                      {isPending && <Spin size="small" className="me-2" />}
                      Save
                    </AppButton>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
}
